import {ReactNode, useRef} from 'react';
import {
  m,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';

/**
 * Shared scroll-motion primitives for appearance blocks.
 *
 * The ambient (looping) effects live in CSS — see the `ap-*` utilities in
 * common.css. The pieces here are the ones that need to know about scroll
 * position: reveal-on-enter and parallax.
 *
 * Everything degrades to "just render it" when the visitor has reduced motion
 * enabled OR when the admin turned the Animations effect off (which puts
 * `.ap-no-animations` on <html>).
 *
 * NOTE: uses `m.*` (not `motion.*`) because the app wraps its tree in
 * framer-motion's LazyMotion with the `domAnimation` feature bundle.
 */

/** True when animations should actually run. */
function useMotionOff(): boolean {
  const reduced = useReducedMotion();
  const gated =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('ap-no-animations');
  return !!reduced || gated;
}

interface RevealProps {
  children: ReactNode;
  /** Stagger position — each step adds 90ms of delay. */
  index?: number;
  /** Direction the content travels in from. */
  from?: 'up' | 'down' | 'left' | 'right' | 'none';
  /** Extra delay in seconds, on top of `index`. */
  delay?: number;
  className?: string;
}

const OFFSET = {
  up: {y: 28, x: 0},
  down: {y: -28, x: 0},
  left: {y: 0, x: -32},
  right: {y: 0, x: 32},
  none: {y: 0, x: 0},
};

/**
 * Fades + slides its children in the first time they scroll into view.
 * Pass `index` to stagger a row/grid of siblings.
 */
export function Reveal({
  children,
  index = 0,
  from = 'up',
  delay = 0,
  className,
}: RevealProps) {
  const off = useMotionOff();
  if (off) return <div className={className}>{children}</div>;

  const {x, y} = OFFSET[from];

  return (
    <m.div
      className={className}
      initial={{opacity: 0, x, y}}
      whileInView={{opacity: 1, x: 0, y: 0}}
      viewport={{once: true, amount: 0.2}}
      transition={{
        duration: 0.6,
        delay: delay + index * 0.09,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {children}
    </m.div>
  );
}

/**
 * Moves its children vertically as the section scrolls past, at a fraction of
 * scroll speed. `speed` is how far (px) it drifts across the whole pass —
 * negative moves against the scroll (the classic parallax feel).
 */
export function Parallax({
  children,
  speed = -60,
  className,
}: {
  children: ReactNode;
  speed?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const off = useMotionOff();

  // Hooks must run unconditionally — we simply ignore the output when off.
  const {scrollYProgress} = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, speed]);

  if (off) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <m.div ref={ref} className={className} style={{y}}>
      {children}
    </m.div>
  );
}

/**
 * Decorative blurred colour orbs for section backgrounds. Purely ornamental,
 * so it's aria-hidden and never intercepts pointer events.
 */
export function AmbientOrbs({
  // `--be-primary` holds space-separated RGB channels (e.g. "80 56 237"), so it
  // has to be wrapped in rgb() rather than used as a colour directly.
  colors = ['rgb(var(--be-primary, 80 56 237))', '#7C3AED'],
  className = '',
}: {
  colors?: string[];
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${className}`}
    >
      <span
        className="ap-drift absolute rounded-full blur-3xl"
        style={{
          width: 340,
          height: 340,
          left: -120,
          top: -120,
          opacity: 0.22,
          backgroundColor: colors[0],
        }}
      />
      <span
        className="ap-drift-slow absolute rounded-full blur-3xl"
        style={{
          width: 380,
          height: 380,
          right: -140,
          top: 60,
          opacity: 0.18,
          backgroundColor: colors[1] ?? colors[0],
        }}
      />
    </div>
  );
}
